import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiUsers, FiPlus, FiSend, FiArrowLeft, FiUserPlus, FiLogOut, FiTrash2 } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { labelFromStatus } from '../services/activity';
import LevelBadge from './LevelBadge';
import VoiceBar from './VoiceBar';
import EmojiPicker from './EmojiPicker';
import { toast } from 'react-toastify';
import ChatMessageBody, { parseContent, isControl } from './ChatMessageBody';
import ChatAuthor from './ChatAuthor';
import { resolveUser, cachedUser } from '../services/userCache';
import VoiceRecorder from './VoiceRecorder';
import { stickerEffect } from '../services/chatFun';
import { useChatSocial, ReactBtn, Reactions } from './ChatSocial';

const H = styled.h3`font-family:${theme.fonts.heading};font-size:1rem;color:${theme.colors.textPrimary};margin:.2rem 0 .6rem;`;
const Muted = styled.p`color:${theme.colors.textMuted};font-size:.85rem;padding:.4rem 0;`;
const Input = styled.input`width:100%;background:${theme.colors.bgPrimary};color:${theme.colors.textPrimary};
  border:1px solid ${theme.colors.border};border-radius:${theme.radii.md};padding:.55rem .8rem;font-size:.9rem;margin-bottom:.5rem;`;
const Btn = styled.button`display:inline-flex;align-items:center;justify-content:center;gap:.4rem;border:none;cursor:pointer;background:${theme.colors.gradient};
  color:#fff;padding:.55rem 1rem;border-radius:${theme.radii.full};font-weight:700;font-size:.85rem;white-space:nowrap;flex-shrink:0;&:disabled{opacity:.5;cursor:default;}`;
const Mini = styled.button`display:inline-flex;align-items:center;gap:.35rem;border:1px solid ${theme.colors.border};background:none;
  color:${theme.colors.textSecondary};cursor:pointer;padding:.4rem .7rem;border-radius:${theme.radii.full};font-size:.78rem;
  &:hover{color:${theme.colors.textPrimary};border-color:${theme.colors.borderHover};}`;
const Row = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.6rem;padding:.6rem .2rem;
  border-bottom:1px solid ${theme.colors.border};.who{display:flex;align-items:center;gap:.5rem;color:${theme.colors.textPrimary};font-weight:600;}
  .dot{width:30px;height:30px;border-radius:50%;background:${theme.colors.gradient};display:flex;align-items:center;justify-content:center;color:#fff;font-size:.8rem;}`;
const ChatBox = styled.div`height:min(52vh,440px);overflow-y:auto;border:1px solid ${theme.colors.border};border-radius:${theme.radii.md};
  padding:.6rem .7rem;background:${theme.colors.bgPrimary};margin:.6rem 0;display:flex;flex-direction:column;gap:.3rem;
  .m{font-size:.85rem;line-height:1.4;overflow-wrap:anywhere;word-break:normal;} .m .u{color:${theme.colors.primaryLight};font-weight:700;margin-right:.35rem;}
  .m.me .u{color:${theme.colors.secondary};}`;
const Foot = styled.form`display:flex;gap:.4rem;`;

// Chat d'un groupe (canal Nakama type 3). Réutilise joinChannel/sendChat/onChatMessage/listChatHistory.
export function GroupChat({ group }) {
  const { auth, user, isPremium } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [pick, setPick] = useState(false);
  const social = useChatSocial();
  const typingRef = useRef(0);
  const chanRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    let unsub; let cancelled = false;
    (async () => {
      const ch = await auth.joinChannel(group.id, 3);
      if (!ch || cancelled) return;
      chanRef.current = ch.id;
      const hist = await auth.listChatHistory(ch.id, 30);
      if (!cancelled) {
        setMsgs(hist.map((m) => {
          const c = parseContent(m.content);
          return { id: m.message_id, user: m.username || 'joueur', sender: m.sender_id, text: c.text, sticker: c.sticker, audio: c.audio, gif: c.gif };
        }).filter((x) => x.text || x.sticker || x.gif || x.audio));
      }
      unsub = auth.onChatMessage((m) => {
        if (m.channel_id !== chanRef.current) return;
        const c = parseContent(m.content);
        if (isControl(c)) { social.handleControl(c, m.username, m.sender_id === (user && user.id)); return; }
        if (!c.text && !c.sticker && !c.gif && !c.audio) return;
        if (c.sticker) stickerEffect(c.sticker);
        setMsgs((prev) => [...prev.slice(-80), { id: m.message_id, user: m.username || 'joueur', sender: m.sender_id, text: c.text, sticker: c.sticker, audio: c.audio, gif: c.gif }]);
      });
    })();
    // Toasts événementiels : un membre entre/sort du chat du groupe (présence de canal).
    const nameOf = (p) => (cachedUser(p.user_id) && cachedUser(p.user_id).name) || (p.username && !/^[0-9a-f-]{6,}$/i.test(p.username) ? p.username : 'Un membre');
    const offChanPres = auth.onChannelPresence((ev) => {
      if (!ev || ev.channel_id !== chanRef.current) return;
      const meId = user && user.id;
      (ev.joins || []).forEach((p) => { if (p.user_id && p.user_id !== meId) { resolveUser(p.user_id); toast.info(`👥 ${nameOf(p)} a rejoint le chat du groupe`, { autoClose: 2500 }); } });
      (ev.leaves || []).forEach((p) => { if (p.user_id && p.user_id !== meId) toast.info(`👋 ${nameOf(p)} a quitté le chat du groupe`, { autoClose: 2500 }); });
    });
    // Après une reconnexion du socket (retour d'onglet/bfcache), on REJOINT le canal du groupe,
    // sinon l'envoi de messages/stickers échoue en silence.
    const offReconnect = auth.onReconnect(async () => {
      const ch = await auth.joinChannel(group.id, 3);
      if (ch && !cancelled) chanRef.current = ch.id;
    });
    return () => { cancelled = true; if (unsub) unsub(); if (offChanPres) offChanPres(); if (offReconnect) offReconnect(); };
  }, [auth, group.id]);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs]);

  // Envoi robuste : si le socket est tombé (retour d'onglet/bfcache) le 1er envoi échoue → on reconnecte,
  // on rejoint le canal et on réessaie UNE fois. Corrige "les emojis/stickers ne partent pas".
  const postChat = async (message, extra) => {
    const payload = isPremium ? { premium: true, ...extra } : extra;
    if (!chanRef.current) { const ch = await auth.joinChannel(group.id, 3); if (ch) chanRef.current = ch.id; }
    if (!chanRef.current) return false;
    let ok = await auth.sendChat(chanRef.current, message, payload);
    if (!ok) {
      await auth.ensureSocket(true);
      const ch = await auth.joinChannel(group.id, 3);
      if (ch) chanRef.current = ch.id;
      ok = await auth.sendChat(chanRef.current, message, payload);
    }
    return ok;
  };
  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (await postChat(t, {})) setText('');
  };
  const sendExtra = async (extra) => { await postChat('', extra); };
  const reactTo = (msgId, emoji) => { if (chanRef.current) { auth.sendChat(chanRef.current, '', { react: emoji, ref: msgId }); social.bump(msgId, emoji); } };
  const notifyTyping = () => { const now = Date.now(); if (chanRef.current && now - typingRef.current > 2500) { typingRef.current = now; auth.sendChat(chanRef.current, '', { typing: true }); } };

  return (
    <>
      <ChatBox ref={bodyRef}>
        {msgs.length === 0 ? <Muted>Aucun message. Dis bonjour au groupe 👋</Muted>
          : msgs.map((m) => (
            <div className={`m${m.sender === (user && user.id) ? ' me' : ''}`} key={m.id}>
              <span className="u"><ChatAuthor senderId={m.sender} fallback={m.user} size={18} /><LevelBadge userId={m.sender} /></span><ChatMessageBody text={m.text} sticker={m.sticker} audio={m.audio} gif={m.gif} />
              <Reactions data={social.reactions[m.id]} /><ReactBtn onReact={(e) => reactTo(m.id, e)} />
            </div>
          ))}
      </ChatBox>
      {social.typingName && <div style={{ fontSize: '.74rem', color: theme.colors.textMuted, fontStyle: 'italic', margin: '.1rem 0' }}>{social.typingName} écrit…</div>}
      <Foot onSubmit={send}>
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <Btn type="button" onClick={() => setPick((p) => !p)} style={{ padding: '.55rem .7rem', background: 'transparent' }} aria-label="Emojis & stickers">😊</Btn>
          {pick && <EmojiPicker onEmoji={(e) => setText((t) => (t + e).slice(0, 280))} onSticker={(id) => { sendExtra({ sticker: id }); setPick(false); }} onGif={(url) => { sendExtra({ gif: url }); setPick(false); }} onClose={() => setPick(false)} />}
        </span>
        <VoiceRecorder onAudio={(url) => sendExtra({ audio: url })} />
        <Input style={{ margin: 0 }} value={text} onChange={(e) => { setText(e.target.value); notifyTyping(); }} placeholder="Message au groupe…" maxLength={280} />
        <Btn type="submit" style={{ padding: '.55rem .8rem' }}><FiSend /></Btn>
      </Foot>
    </>
  );
}

export default function GroupsPanel() {
  const { auth, isLoggedIn } = useAuth();
  const [groups, setGroups] = useState([]);
  const [friends, setFriends] = useState([]);
  const [name, setName] = useState('');
  const [open, setOpen] = useState(null);   // groupe ouvert (chat)
  const [members, setMembers] = useState([]);
  const [onlineMembers, setOnlineMembers] = useState({}); // { user_id: activité } des membres EN LIGNE
  const [inviteId, setInviteId] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => { setGroups(await auth.listMyGroups()); }, [auth]);
  useEffect(() => { if (isLoggedIn) { load(); auth.listFriends(0).then(setFriends); } }, [isLoggedIn, load, auth]);
  // Membres du groupe ouvert + suivi de leur présence EN LIGNE (statut + activité).
  useEffect(() => {
    if (!open) { setOnlineMembers({}); return undefined; }
    // Charge les membres + (re)suit la présence des membres ET des amis → colonne « Amis en ligne ».
    const refresh = async () => {
      const ms = await auth.listGroupMembers(open.id);
      setMembers(ms);
      const friendIds = friends.map((f) => f.user && f.user.id).filter(Boolean);
      const ids = [...new Set([...ms.map((m) => m.id), ...friendIds])].filter(Boolean);
      if (ids.length) {
        const pres = await auth.followUsers(ids);
        setOnlineMembers(Object.fromEntries((pres || []).map((p) => [p.user_id, labelFromStatus(p.status) || 'En ligne'])));
      }
    };
    refresh();
    const unsub = auth.onStatusPresence((ev) => setOnlineMembers((prev) => {
      const n = { ...prev };
      (ev.joins || []).forEach((p) => { n[p.user_id] = labelFromStatus(p.status) || 'En ligne'; });
      (ev.leaves || []).forEach((p) => { delete n[p.user_id]; });
      return n;
    }));
    // Reconnexion socket ou retour sur l'onglet → on re-suit (sinon la présence se fige jusqu'au reload).
    const offRe = auth.onReconnect(refresh);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { if (unsub) unsub(); if (offRe) offRe(); document.removeEventListener('visibilitychange', onVis); };
  }, [open, auth, friends]);

  const create = async () => {
    const n = name.trim(); if (!n) return;
    const g = await auth.createGroup(n);
    if (g) { setName(''); load(); } else setMsg('Création impossible.');
  };
  const invite = async (id) => {
    const who = (typeof id === 'string' && id) || inviteId;
    if (!open || !who) return;
    const ok = await auth.inviteToGroup(open.id, [who]);
    setMsg(ok ? '✅ Ami ajouté au groupe.' : 'Échec de l\'ajout.');
    if (ok) { setInviteId(''); auth.listGroupMembers(open.id).then(setMembers); }
  };
  const leave = async () => {
    if (!open) return;
    if (await auth.leaveGroup(open.id)) { setOpen(null); load(); }
  };
  const remove = async () => {
    if (!open) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer le groupe « ${open.name} » ?\n\nL'HISTORIQUE DE DISCUSSION du groupe sera aussi supprimé — c'est IRRÉVERSIBLE.`)) return;
    try { await auth.rpc('vg_channel_purge', { type: 'group', target: open.id }); } catch { /* best-effort */ }
    if (await auth.deleteGroup(open.id)) { setOpen(null); load(); }
    else setMsg('Suppression impossible (réservée au créateur du groupe).');
  };

  if (!isLoggedIn) return <Muted>Connecte-toi pour créer des groupes d'amis.</Muted>;

  // ── Vue d'un groupe (membres + invitation + chat) ──
  if (open) {
    const memberIds = new Set(members.map((m) => m.id));
    const invitable = friends.filter((f) => f.state === 0 && !memberIds.has(f.user.id));
    // On ne liste QUE les amis EN LIGNE (statut présent) — les hors-ligne n'apparaissent pas ici.
    const friendRows = friends.filter((f) => f.state === 0 && onlineMembers[f.user.id]);
    return (
      <div>
        <Mini onClick={() => { setOpen(null); setMsg(''); }}><FiArrowLeft /> Retour aux groupes</Mini>
        <H style={{ marginTop: '.6rem' }}><FiUsers /> {open.name} <Muted as="span" style={{ padding: 0, fontSize: '.78rem' }}>· {members.length} membre(s)</Muted></H>

        {/* 2 colonnes : AMIS EN LIGNE à gauche · chat (plus étroit) à droite. Empilé sur mobile. */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 236px', maxWidth: '100%' }}>
            <H style={{ fontSize: '.92rem' }}>🟢 Amis en ligne</H>
            {friendRows.length === 0 && <Muted style={{ fontSize: '.8rem' }}>Aucun ami en ligne pour l'instant.</Muted>}
            {friendRows.map((f) => {
              const act = onlineMembers[f.user.id]; const inGroup = memberIds.has(f.user.id);
              return (
                <Row key={f.user.id} style={{ padding: '.45rem .1rem' }}>
                  <span className="who">
                    <span className="dot" style={{ position: 'relative', width: 28, height: 28, fontSize: '.72rem' }}>{(f.user.username || '?').slice(0, 2).toUpperCase()}
                      <span style={{ position: 'absolute', right: -2, bottom: -2, width: 9, height: 9, borderRadius: '50%', border: `2px solid ${theme.colors.bgCard}`, background: act ? '#22c55e' : '#6b7280' }} />
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                      <span style={{ fontSize: '.82rem' }}>{f.user.display_name || f.user.username}<LevelBadge userId={f.user.id} /></span>
                      <span style={{ fontSize: '.68rem', color: act ? '#22c55e' : theme.colors.textMuted }}>{act ? `🟢 ${act}` : '⚪ hors ligne'}</span>
                    </span>
                  </span>
                  {inGroup
                    ? <span style={{ fontSize: '.64rem', color: theme.colors.textMuted }}>dans le groupe</span>
                    : <Mini style={{ padding: '.2rem .5rem', fontSize: '.7rem' }} title="Inviter dans le groupe" onClick={() => invite(f.user.id)}><FiUserPlus /></Mini>}
                </Row>
              );
            })}
            {/* Repli : sélecteur d'invitation (utile si beaucoup d'amis) */}
            {invitable.length > 0 && (
              <div style={{ display: 'flex', gap: '.4rem', marginTop: '.6rem' }}>
                <select value={inviteId} onChange={(e) => setInviteId(e.target.value)}
                  style={{ flex: 1, minWidth: 0, background: theme.colors.bgPrimary, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.full, padding: '.4rem .7rem', fontSize: '.78rem' }}>
                  <option value="">Inviter un ami…</option>
                  {invitable.map((f) => <option key={f.user.id} value={f.user.id}>{f.user.display_name || f.user.username}</option>)}
                </select>
                <Mini onClick={() => invite()}><FiUserPlus /></Mini>
              </div>
            )}
            {msg && <Muted style={{ fontSize: '.78rem' }}>{msg}</Muted>}
          </div>

          <div style={{ flex: '1 1 300px', minWidth: 0, maxWidth: 560 }}>
            <VoiceBar channelName={`vgvoice_${open.id}`} label="le vocal du groupe" />
            <H style={{ fontSize: '.92rem' }}>💬 Chat du groupe</H>
            <GroupChat group={open} />
          </div>
        </div>

        <div style={{ marginTop: '.8rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          <Mini onClick={leave}><FiLogOut /> Quitter le groupe</Mini>
          {open.myState === 0 && <Mini onClick={remove} style={{ color: '#ef4444', borderColor: '#ef4444' }}><FiTrash2 /> Supprimer le groupe</Mini>}
        </div>
      </div>
    );
  }

  // ── Liste des groupes + création ──
  return (
    <div>
      <H><FiUsers /> Mes groupes d'amis</H>
      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '.8rem' }}>
        <Input style={{ margin: 0, flex: 1, minWidth: 0 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du nouveau groupe…" maxLength={40} onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
        <Btn onClick={create} disabled={!name.trim()}><FiPlus /> Créer</Btn>
      </div>
      {msg && <Muted>{msg}</Muted>}
      {groups.length === 0 ? <Muted>Aucun groupe. Crée-en un et invite tes amis à discuter ! 👥</Muted>
        : groups.map((g) => (
          <Row key={g.id}>
            <span className="who"><span className="dot"><FiUsers size={14} /></span>{g.name} <Muted as="span" style={{ padding: 0, fontSize: '.76rem' }}>· {g.edge_count || 0} membre(s)</Muted></span>
            <span style={{ display: 'flex', gap: '.4rem' }}>
              <Mini onClick={() => { setOpen(g); setMsg(''); }}>Ouvrir</Mini>
              {g.myState === 0 && <Mini title="Supprimer le groupe" style={{ color: '#ef4444', borderColor: '#ef4444' }}
                onClick={async () => { /* eslint-disable-next-line no-alert */ if (window.confirm(`Supprimer le groupe « ${g.name} » ?\n\nL'HISTORIQUE DE DISCUSSION du groupe sera aussi supprimé — c'est IRRÉVERSIBLE.`)) { try { await auth.rpc('vg_channel_purge', { type: 'group', target: g.id }); } catch (e) { /* */ } if (await auth.deleteGroup(g.id)) load(); else setMsg('Suppression réservée au créateur.'); } }}><FiTrash2 /></Mini>}
            </span>
          </Row>
        ))}
    </div>
  );
}
